import type {
  KeyConfig,
  KeyboardType,
} from "@eweadn-qiankun/shared-components/components/Keyboard/types";
import {
  LAYOUT_87_KEYCODES,
  LAYOUT_HUAFENDA87_KEYCODES,
  LAYOUT_84_KEYCODES,
} from "@eweadn-qiankun/shared-components/components/Keyboard/keyboardLayouts";
import { KEYBOARD_LAYOUTS } from "@eweadn-qiankun/shared-components/utils/keyCodeMapper";
import {
  convertRowLayoutToKeyConfigs,
  generateDefaultLayout,
  getKeyWidthsByType,
} from "@eweadn-qiankun/shared-components/utils/keyboardLayoutUtils";

/** 与 KEYBOARD_LAYOUTS["68"] 21 列对齐 */
const RHYTHM_PREVIEW_68_CAPS_ROW_WIDTHS = [
  1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 2.3, 1, 0, 0, 0, 0, 0, 0,
] as const;

const RHYTHM_PREVIEW_68_SHIFT_ROW_WIDTHS = [
  2.15, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.9, 1, 1, 0, 0, 0, 0, 0, 0,
] as const;

const RHYTHM_PREVIEW_68_BOTTOM_ROW_WIDTHS = [
  1.25, 1.25, 1.25, 0, 0, 0, 6.62, 0, 0, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0,
] as const;

const RHYTHM_PREVIEW_HUAFENDA68_BOTTOM_ROW_WIDTHS = [
  1.25, 1.25, 1.25, 0, 0, 0, 6.62, 0, 0, 1.25, 1.25, 0.5, 1, 1, 1, 0, 0, 0, 0,
  0, 0,
] as const;

function getRhythmPreviewRowWidths(keyboardType: KeyboardType): number[][] {
  const widths = getKeyWidthsByType(keyboardType).map((row) => [...row]);
  if (
    keyboardType === "68" ||
    keyboardType === "huafenda68" ||
    keyboardType === "tank68he"
  ) {
    widths[3] = [...RHYTHM_PREVIEW_68_CAPS_ROW_WIDTHS];
    widths[4] = [...RHYTHM_PREVIEW_68_SHIFT_ROW_WIDTHS];
    widths[5] =
      keyboardType === "huafenda68"
        ? [...RHYTHM_PREVIEW_HUAFENDA68_BOTTOM_ROW_WIDTHS]
        : [...RHYTHM_PREVIEW_68_BOTTOM_ROW_WIDTHS];
  }
  if (keyboardType === "68EVO") {
    widths[3] = [...RHYTHM_PREVIEW_68_CAPS_ROW_WIDTHS];
    widths[4] = [...RHYTHM_PREVIEW_68_SHIFT_ROW_WIDTHS];
  }
  return widths;
}

function getRhythmPreviewKeycodeMatrix(
  keyboardType: string
): number[][] | null {
  if (keyboardType === "87") return LAYOUT_87_KEYCODES;
  if (keyboardType === "huafenda87") return LAYOUT_HUAFENDA87_KEYCODES;
  if (keyboardType === "84" || keyboardType === "deep80max") {
    return LAYOUT_84_KEYCODES;
  }
  const kt = keyboardType as keyof typeof KEYBOARD_LAYOUTS;
  const fromMapper = KEYBOARD_LAYOUTS[kt];
  return fromMapper?.length ? (fromMapper as number[][]) : null;
}

/**
 * 全局音乐律动虚拟键盘：与主应用内页一致的紧凑矩阵（如 75Pro 15 列，非 21 列补零表）。
 * 律动着色与 Keyboard 展示须共用此布局，避免 generateDefaultLayout 补零矩阵导致键色错位全白。
 */
export function buildRhythmPreviewKeyboardLayout(
  keyboardType: string
): KeyConfig[][] {
  const kt = keyboardType as KeyboardType;
  if (kt === "x75v3" || kt === "gamma75") {
    return generateDefaultLayout(kt);
  }
  const matrix = getRhythmPreviewKeycodeMatrix(keyboardType);
  if (!matrix?.length) {
    return generateDefaultLayout(kt);
  }
  const widths = getRhythmPreviewRowWidths(kt);
  return matrix.map((row, rowIndex) =>
    convertRowLayoutToKeyConfigs(row, widths[rowIndex] ?? [1], rowIndex, kt)
  );
}
